import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getLesson, getLessonImage } from '../../services/studentService';
import { exportPagesToPdf } from '../../utils/domToPdf';
import VariantBlank, { plural } from '../../components/Student/VariantBlank';
import toast from 'react-hot-toast';
import '../../styles/VariantPrint.css';

/* Страница бланка: те же листы, что уходят в PDF, плюс панель сверху. Нужна
   тем, кто хочет сперва посмотреть или печатать напрямую; кнопка «Скачать PDF»
   в списках вариантов работает и без неё (см. utils/variantPdf). */
export default function VariantPrintPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageEls, setPageEls] = useState([]);
  const [saving, setSaving] = useState(0);
  const pagesRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    getLesson(lessonId)
      .then(res => setLesson(res.data))
      .catch(() => setLesson(null))
      .finally(() => setLoading(false));
  }, [lessonId]);

  // На телефоне лист шире экрана — ужимаем превью. При сборке PDF и печати
  // масштаб снимаем, иначе размеры поедут.
  useEffect(() => {
    const fit = () => {
      const el = pagesRef.current;
      if (el) el.style.zoom = Math.min(1, (window.innerWidth - 24) / 794);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [pageEls]);

  useEffect(() => {
    if (pageEls.length && params.get('auto') === '1') {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [pageEls, params]);

  const download = async () => {
    if (!pageEls.length) return;
    setSaving(1);
    const zoom = pagesRef.current?.style.zoom;
    if (pagesRef.current) pagesRef.current.style.zoom = 1;
    try {
      const name = `${(lesson?.title || 'Вариант').replace(/[\\/:*?"<>|]/g, ' ').trim()}.pdf`;
      await exportPagesToPdf(pageEls, name, (done, all) => setSaving(Math.max(1, Math.round((done / all) * 100))),
        { imageProxy: (url) => getLessonImage(lessonId, url) });
      toast.success('Файл сохранён');
    } catch {
      toast.error('Не удалось собрать PDF — попробуйте «Печать»');
    } finally {
      if (pagesRef.current) pagesRef.current.style.zoom = zoom || 1;
      setSaving(0);
    }
  };

  if (loading) return <div className="pdfb-root" style={{ padding: 60, textAlign: 'center', color: '#9690a8' }}>Готовим бланк…</div>;
  if (!lesson) return <div className="pdfb-root" style={{ padding: 60, textAlign: 'center', color: '#9690a8' }}>Вариант не найден</div>;

  return (
    <div className="pdfb-root">
      <div className="pdfb-bar">
        <button className="pdfb-btn ghost" onClick={() => navigate(`/variant/${lessonId}`)}>Назад</button>
        <div className="pdfb-bar-t">{lesson.title}</div>
        <span className="pdfb-bar-s">
          {pageEls.length ? `${pageEls.length} ${plural(pageEls.length, ['страница', 'страницы', 'страниц'])}` : 'считаем страницы…'}
        </span>
        <button className="pdfb-btn ghost" onClick={() => window.print()} title="Открыть печать браузера">Печать</button>
        <button className="pdfb-btn" disabled={!pageEls.length || !!saving} onClick={download}>
          {saving ? `Собираем… ${saving}%` : 'Скачать PDF'}
        </button>
      </div>

      <VariantBlank lesson={lesson} onReady={setPageEls} pagesRef={pagesRef} />
    </div>
  );
}
